import React, { useEffect, useState, useCallback } from 'react';
import { webOrigin } from '../../utils/brand';
import { Link, useNavigate, useParams } from 'react-router-dom';
import portalService from '../../services/portalService';
import applicationService from '../../services/applicationService';
import messageService from '../../services/messageService';
import { useDialog } from '../../context';
import PortalShell from './PortalShell';
import { Avatar, initials } from './PortalBits';
import CheckoutForm from '../../components/CheckoutForm';
import AddPeopleModal from '../../components/AddPeopleModal';
import Linkify from '../../components/Linkify';
import { LinksPills } from '../../components/LinksEditor';
import { useAuth } from '../../context';

/**
 * /portal/signals/:eventId — Signal detail.
 * Reads viewer state from the SignalCardDto and renders the right CTA:
 *   isMine                            → MANAGE: applicants list, invites list, INVITE PEOPLE, COPY LINK
 *   viewerInvited (pending invite)    → YOU'RE INVITED (I'M IN / DECLINE — fast-track past curation)
 *   viewerState "accepted"            → YOU'RE IN + MESSAGE HOST + WITHDRAW
 *   viewerState "applied_pending"     → WAITING ON HOST + WITHDRAW
 *   viewerState "open" + open mode    → I'M DOWN (immediate accept)
 *   viewerState "open" + curated mode → I'M DOWN (host reviews; pending)
 *   isFull (and not in any of above)  → FULL
 */
export default function PortalSignalDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { showConfirm, showAlert } = useDialog();
  const { user } = useAuth();
  const [signal, setSignal] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Application id is needed by CheckoutForm. We fetch it whenever the viewer
  // is in a pending_payment state on a paid Signal.
  const [pendingPaymentAppId, setPendingPaymentAppId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await portalService.getSignal(eventId);
    if (r.success) {
      setSignal(r.data);
      if (r.data?.isMine) {
        const [ap, inv] = await Promise.all([
          portalService.listSignalApplicants(eventId),
          portalService.listSignalInvites(eventId),
        ]);
        if (ap.success) setApplicants(ap.data || []);
        if (inv.success) setInvites(inv.data || []);
      }
    } else {
      setError(r.error || 'Signal not found');
    }
    setLoading(false);
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  // If we land on the page already in pending_payment (e.g. reload after a
  // failed checkout), pre-fetch the application id so the CheckoutForm has
  // what it needs without waiting on the user to click anything.
  useEffect(() => {
    if (signal?.viewerState === 'applied_pending' && (signal.costCents || 0) > 0 && !pendingPaymentAppId) {
      fetchPendingPaymentAppId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal?.viewerState, signal?.costCents]);

  const onImDown = async () => {
    setBusy(true); setError(null);
    const r = await portalService.respondImDown(eventId);
    setBusy(false);
    if (r.success) {
      setSignal(r.data);
      // Paid signals land in pending_payment — fetch the app id for CheckoutForm.
      if ((r.data?.costCents || 0) > 0) {
        await fetchPendingPaymentAppId();
      }
    } else {
      setError(r.error || 'Could not respond');
    }
  };

  // External-ticketing signals: open the off-platform link and, if the viewer
  // isn't already in the room, tap them in so the host sees who clicked through.
  const onExternalTickets = async () => {
    if (signal?.externalTicketUrl) window.open(signal.externalTicketUrl, '_blank', 'noopener,noreferrer');
    const alreadyIn = ['accepted', 'applied_pending'].includes(signal?.viewerState);
    if (!alreadyIn) {
      setBusy(true); setError(null);
      const r = await portalService.respondImDown(eventId);
      setBusy(false);
      if (r.success) setSignal(r.data);
    }
  };

  const fetchPendingPaymentAppId = async () => {
    const r = await applicationService.getMyApplicationForEvent(eventId);
    if (r.success && r.data?.id) setPendingPaymentAppId(r.data.id);
  };

  const onWithdraw = async () => {
    const ok = await showConfirm({ title: 'Step back out?', message: 'You can tap back in later if you change your mind.', confirmLabel: 'STEP BACK OUT', variant: 'danger' });
    if (!ok) return;
    setBusy(true); setError(null);
    const r = await portalService.withdrawResponse(eventId);
    setBusy(false);
    if (r.success) setSignal(r.data);
    else setError(r.error || 'Could not withdraw');
  };

  const review = async (userId, action) => {
    setBusy(true); setError(null);
    const r = await portalService.reviewSignalApplicant(eventId, userId, action);
    setBusy(false);
    if (r.success) {
      setSignal(r.data);
      const ap = await portalService.listSignalApplicants(eventId);
      if (ap.success) setApplicants(ap.data || []);
    } else {
      setError(r.error || 'Could not save');
    }
  };

  const respondInvite = async (accept) => {
    setBusy(true); setError(null);
    const r = await portalService.respondToSignalInvite(eventId, accept);
    setBusy(false);
    if (r.success) {
      setSignal(r.data);
      if (accept && (r.data?.costCents || 0) > 0) {
        await fetchPendingPaymentAppId();
      }
    } else {
      setError(r.error || 'Could not respond to invite');
    }
  };

  const sendInvites = async (userIds) => {
    if (!userIds.length) return;
    setBusy(true); setError(null);
    const r = await portalService.inviteToSignal(eventId, userIds);
    setBusy(false);
    if (r.success) {
      setInvites(r.data || []);
      setInviteOpen(false);
    } else {
      setError(r.error || 'Could not send invites');
    }
  };

  const onDelete = async () => {
    const ok = await showConfirm({
      title: 'Delete this Signal?',
      message: "It disappears from everyone's feed for good. Anyone who tapped in loses the room. This can't be undone.",
      confirmLabel: 'DELETE SIGNAL',
      cancelLabel: 'KEEP IT',
      variant: 'danger',
    });
    if (!ok) return;
    setBusy(true); setError(null);
    const r = await portalService.deleteSignal(eventId);
    setBusy(false);
    if (r.success) navigate('/noir/home', { replace: true });
    else setError(r.error || 'Could not delete this Signal');
  };

  const copyLink = async () => {
    const url = `${webOrigin()}/portal/signals/${eventId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      await showAlert({ title: 'Copy this link', message: url });
    }
  };

  // Host opens the group chat for accepted attendees (created on first open;
  // later acceptances join automatically — membership is roster-derived).
  const openGroupChat = async () => {
    const r = await messageService.openEventGroup(eventId);
    if (r.success && r.data?.threadId) navigate(`/messages?thread=${r.data.threadId}`);
    else await showAlert({ title: 'Could not open the room', message: r.error || 'Try that again?' });
  };

  // Attendee "MESSAGE HOST" — group room if the host opened one, else the
  // idempotent 1:1 host thread. Never spawns a duplicate.
  const messageHost = async () => {
    const r = await messageService.resolveSignalHostThread(eventId);
    if (r.success && r.data?.threadId) navigate(`/messages?thread=${r.data.threadId}`);
    else await showAlert({ title: 'Could not open the chat', message: r.error || 'Try that again?' });
  };

  if (loading) {
    return (
      <PortalShell viewerState="accepted">
        <div style={{ padding: 80, textAlign: 'center' }} className="portal-mono">LOADING…</div>
      </PortalShell>
    );
  }

  if (!signal) {
    return (
      <PortalShell viewerState="accepted">
        <div style={{ maxWidth: 720, margin: '80px auto', padding: '0 24px' }}>
          <p className="portal-label" style={{ marginBottom: 14 }}>NOT FOUND</p>
          <h1 className="portal-hero" style={{ marginBottom: 18 }}>This Signal has gone quiet.</h1>
          <p className="portal-prose">{error || 'It may have been canceled, or the link is stale.'}</p>
          <Link to="/noir/home" className="portal-mono portal-gold" style={{ display: 'inline-block', marginTop: 24, fontSize: 11 }}>← BACK TO PORTAL NOIR</Link>
        </div>
      </PortalShell>
    );
  }

  const isFree = (signal.costCents || 0) === 0;
  const dateLabel = signal.dateTimeUtc ? new Date(signal.dateTimeUtc).toLocaleString() : '';

  return (
    <PortalShell viewerState="accepted">
      <div className="wrap enter" style={{ maxWidth: 820, paddingTop: 'clamp(36px,6vw,64px)', paddingBottom: 100 }}>
        <button className="backlink" onClick={() => navigate('/noir/home')}>← BACK TO THE FEED</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <span className={'tag ' + (signal.mode === 'curated' ? 'tag--cur' : 'tag--open')}>{signal.mode === 'curated' ? 'CURATED' : 'OPEN'}</span>
          <span className="meta">SIGNAL</span>
        </div>
        <h1 className="display" style={{ fontSize: 'clamp(46px,7vw,96px)', maxWidth: '14ch', marginBottom: 36 }}>{signal.primaryCityLabel || 'A Portal Noir'}<span className="dot">.</span></h1>

        <article className="sigcard" style={{ cursor: 'default', maxWidth: 720, marginBottom: 8 }}>
          <span className="sigcard__seam" style={{ opacity: 1 }} />
          <div className="sigcard__top">
            <Avatar src={signal.hostAvatarUrl} init={initials(signal.hostDisplayName)} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="sigcard__name">
                {signal.hostHandle
                  ? <Link to={`/${signal.hostHandle}`} style={{ color: 'inherit' }}>{signal.hostDisplayName} <span className="sigcard__handle">@{signal.hostHandle}</span></Link>
                  : signal.hostDisplayName}
              </div>
              <div className="meta" style={{ marginTop: 3 }}>
                {(signal.cities || []).join(' · ')}{signal.venueName ? ` · ${signal.venueName}` : ''} &nbsp;·&nbsp; {dateLabel}
              </div>
            </div>
          </div>
          {signal.links?.length > 0 && <div style={{ marginTop: 16 }}><LinksPills links={signal.links} theme="portal" /></div>}
          <p className="sigcard__note">“<Linkify text={signal.signalText} linkClassName="portal-gold" linkStyle={{ textDecoration: 'underline' }} />”</p>
          <div className="sigcard__foot">
            {signal.capacitySlots > 0
              ? <span className="meta">{signal.acceptedCount} OF {signal.capacitySlots} IN</span>
              : <span className="meta" style={{ color: 'var(--cream-faint)' }}>OPEN ROOM</span>}
            {!isFree
              ? <span className="meta">${(signal.costCents / 100).toFixed(0)} / HEAD</span>
              : <span className="meta" style={{ color: 'var(--cream-faint)' }}>NO COVER</span>}
          </div>
        </article>

        {error && <p className="meta" style={{ color: '#B4736A', margin: '20px 0' }}>{error}</p>}

        {/* CTA block — host management or viewer-state aware */}
        {signal.isMine ? (
          <HostManagePanel
            mode={signal.mode}
            applicants={applicants}
            invites={invites}
            onReview={review}
            onOpenInvite={() => setInviteOpen(true)}
            onCopyLink={copyLink}
            onEdit={() => navigate(`/noir/signals/${eventId}/edit`)}
            onDelete={onDelete}
            onMessageAttendees={openGroupChat}
            copied={copied}
            busy={busy}
          />
        ) : signal.isExternalTicketing ? (
          <div className="status">
            <div className="status__h">GET TICKETS</div>
            <p className="status__t">
              Tickets for this one are sold off-portal.
              {signal.externalTicketDisplayPrice ? ` ${signal.externalTicketDisplayPrice}.` : ''}
              {' '}Tapping through adds you to the room so {(signal.hostDisplayName || 'the host').split(' ')[0]} knows you're coming.
            </p>
            <div className="status__row">
              <button onClick={onExternalTickets} disabled={busy} className="btn btn--gold">
                {busy ? 'WORKING…' : 'GET TICKETS'} <span className="arr">→</span>
              </button>
              {['accepted', 'applied_pending'].includes(signal.viewerState) && (
                <button onClick={onWithdraw} disabled={busy} className="btn">STEP BACK OUT</button>
              )}
            </div>
          </div>
        ) : signal.viewerInvited ? (
          <div>
            <p className="portal-label" style={{ marginBottom: 12 }}>YOU'RE INVITED.</p>
            <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', fontStyle: 'italic', marginBottom: 20 }}>
              {signal.hostDisplayName} picked you. Accept and you're in — no curation, no waiting.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => respondInvite(true)} disabled={busy} className="btn btn--gold">{busy ? 'WORKING…' : "I'M IN →"}</button>
              <button onClick={() => respondInvite(false)} disabled={busy} className="btn">DECLINE</button>
            </div>
          </div>
        ) : signal.viewerState === 'accepted' ? (
          <div className="status">
            <div className="status__h">YOU’RE IN PORTAL NOIR</div>
            <p className="status__t">
              Message {(signal.hostDisplayName || 'the host').split(' ')[0]} to nail down the where and the when. The chat’s open now.
            </p>
            <div className="status__row">
              <button onClick={messageHost} className="btn btn--gold">MESSAGE {(signal.hostDisplayName || 'HOST').split(' ')[0].toUpperCase()} <span className="arr">→</span></button>
              <button onClick={onWithdraw} disabled={busy} className="btn">STEP BACK OUT</button>
            </div>
          </div>
        ) : signal.viewerState === 'applied_pending' && (signal.costCents || 0) > 0 ? (
          <div>
            <p className="portal-label" style={{ marginBottom: 12 }}>FINISH PAYING TO LOCK YOUR SEAT</p>
            <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', fontStyle: 'italic', marginBottom: 16 }}>
              ${(signal.costCents / 100).toFixed(2)} secures the room. Apple Pay, Google Pay, or card.
            </p>
            {pendingPaymentAppId ? (
              <div style={{ background: '#ECE7DA', color: '#000', padding: 18, border: '2px solid #C9A24B' }}>
                <CheckoutForm
                  eventId={eventId}
                  applicationId={pendingPaymentAppId}
                  quantity={1}
                  userEmail={user?.email}
                  onSuccess={() => { load(); }}
                  onError={(m) => setError(m)}
                />
              </div>
            ) : (
              <p className="portal-mono" style={{ color: 'rgba(236,231,218,0.34)', fontSize: 11 }}>LOADING CHECKOUT…</p>
            )}
            <div style={{ marginTop: 14 }}>
              <button onClick={onWithdraw} disabled={busy} className="btn">CANCEL</button>
            </div>
          </div>
        ) : signal.viewerState === 'applied_pending' ? (
          <div>
            <p className="portal-label" style={{ marginBottom: 12 }}>WAITING ON HOST.</p>
            <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', fontStyle: 'italic', marginBottom: 24 }}>
              {signal.hostDisplayName} is reviewing. You'll get a notification either way.
            </p>
            <button onClick={onWithdraw} disabled={busy} className="btn">WITHDRAW</button>
          </div>
        ) : signal.isFull ? (
          <div>
            <p className="portal-label" style={{ marginBottom: 12 }}>FULL.</p>
            <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', fontStyle: 'italic' }}>
              This Portal Noir hit capacity. Watch for the next one.
            </p>
          </div>
        ) : (
          <div className="status">
            <div className="status__h">{signal.mode === 'curated' ? 'ASK TO JOIN' : 'JUMP INTO PORTAL NOIR'}</div>
            <p className="status__t">
              {signal.mode === 'curated'
                ? `${signal.hostDisplayName} picks who's in. Say why you'd be good in the room and wait for the nod.`
                : `${signal.capacitySlots > 0 ? `${signal.acceptedCount} of ${signal.capacitySlots} already have. ` : ''}The chat opens the second you do.`}
            </p>
            <div className="status__row">
              <button onClick={onImDown} disabled={busy} className="btn btn--gold">
                {busy ? 'SENDING…' : (signal.mode === 'curated' ? 'ASK TO COME' : "I'M IN")} <span className="arr">→</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <AddPeopleModal
        variant="portal"
        title="Invite people"
        ctaLabel="Invite"
        placeholder="Search Portal Noir members…"
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onAdd={sendInvites}
        searchFn={(q, limit) => portalService.searchPortalMembers(q, limit)}
        excludeUserIds={invites.map((i) => i.invitedUserId)}
      />
    </PortalShell>
  );
}

function HostManagePanel({ mode, applicants, invites, onReview, onOpenInvite, onCopyLink, onEdit, onDelete, onMessageAttendees, copied, busy }) {
  const pending = applicants.filter((a) => a.status === 'pending' || a.status === 'pending_payment');
  // 'paid' is the terminal accepted state on paid Signals (after CheckoutService
  // promotes accepted/pending_payment → paid). Bucket it as IN THE ROOM so paid
  // members don't get incorrectly listed under Withdrawn / Declined.
  const accepted = applicants.filter((a) => a.status === 'accepted' || a.status === 'paid');
  const others = applicants.filter((a) =>
    a.status !== 'pending' && a.status !== 'pending_payment' &&
    a.status !== 'accepted' && a.status !== 'paid');
  const pendingInvites = invites.filter((i) => i.status === 'pending');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <p className="portal-label" style={{ margin: 0 }}>YOUR ROOM</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={onOpenInvite} disabled={busy} className="btn btn--gold" style={{ padding: '10px 18px', fontSize: 11 }}>INVITE PEOPLE →</button>
          {onMessageAttendees && (
            <button onClick={onMessageAttendees} disabled={busy} className="btn" style={{ padding: '10px 18px', fontSize: 11 }}>MESSAGE ATTENDEES</button>
          )}
          <button onClick={onCopyLink} disabled={busy} className="btn" style={{ padding: '10px 18px', fontSize: 11 }}>
            {copied ? 'LINK COPIED ✓' : 'COPY LINK'}
          </button>
          {onEdit && (
            <button onClick={onEdit} disabled={busy} className="btn" style={{ padding: '10px 18px', fontSize: 11 }}>EDIT</button>
          )}
        </div>
      </div>

      {mode === 'curated' && pending.length > 0 && (
        <>
          <p className="portal-mono" style={{ fontSize: 11, color: 'rgba(236,231,218,0.34)', marginBottom: 8 }}>WAITING FOR YOU ({pending.length})</p>
          <div className="portal-card" style={{ padding: 0, marginBottom: 18 }}>
            {pending.map((a) => (
              <ApplicantRow key={a.userId} a={a} onAccept={() => onReview(a.userId, 'accept')} onDecline={() => onReview(a.userId, 'decline')} busy={busy} />
            ))}
          </div>
        </>
      )}

      <p className="portal-mono" style={{ fontSize: 11, color: 'rgba(236,231,218,0.34)', marginBottom: 8 }}>IN THE ROOM ({accepted.length})</p>
      <div className="portal-card" style={{ padding: 0, marginBottom: 18 }}>
        {accepted.length === 0 ? (
          <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.56)', fontStyle: 'italic', padding: 18 }}>Nobody yet.</p>
        ) : accepted.map((a) => (
          <ApplicantRow key={a.userId} a={a} busy={busy} />
        ))}
      </div>

      {pendingInvites.length > 0 && (
        <>
          <p className="portal-mono" style={{ fontSize: 11, color: 'rgba(236,231,218,0.34)', marginBottom: 8 }}>INVITED, NOT YET RESPONDED ({pendingInvites.length})</p>
          <div className="portal-card" style={{ padding: 0, marginBottom: 18 }}>
            {pendingInvites.map((i) => (
              <InviteRow key={i.id} i={i} />
            ))}
          </div>
        </>
      )}

      {others.length > 0 && (
        <>
          <p className="portal-mono" style={{ fontSize: 11, color: 'rgba(236,231,218,0.34)', marginBottom: 8 }}>WITHDRAWN / DECLINED ({others.length})</p>
          <div className="portal-card" style={{ padding: 0 }}>
            {others.map((a) => (
              <ApplicantRow key={a.userId} a={a} busy={busy} muted />
            ))}
          </div>
        </>
      )}

      <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid rgba(236,231,218,0.12)' }}>
        <button
          onClick={onDelete}
          disabled={busy}
          className="portal-mono"
          style={{ background: 'none', border: '1px solid rgba(180,115,106,0.5)', color: '#B4736A', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '10px 18px', letterSpacing: '0.18em', textTransform: 'uppercase' }}
        >
          {busy ? 'WORKING…' : 'DELETE SIGNAL'}
        </button>
        <p className="portal-serif" style={{ color: 'rgba(236,231,218,0.4)', fontStyle: 'italic', fontSize: 13, marginTop: 10 }}>
          Pulls it from every feed for good. Can't be undone.
        </p>
      </div>
    </div>
  );
}

function ApplicantRow({ a, onAccept, onDecline, busy, muted }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid rgba(236,231,218,0.12)', opacity: muted ? 0.5 : 1 }}>
      <div className="portal-avatar">
        {a.avatarUrl ? <img src={a.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {a.handle ? (
          <Link to={`/${a.handle}`} className="portal-serif" style={{ fontSize: 15, color: '#ECE7DA', textDecoration: 'none' }}>{a.displayName}</Link>
        ) : (
          <span className="portal-serif" style={{ fontSize: 15 }}>{a.displayName}</span>
        )}
        <div className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)' }}>
          {a.handle ? `@${a.handle}` : ''}{a.location ? `${a.handle ? ' · ' : ''}${a.location.toUpperCase()}` : ''} · {a.status.toUpperCase()}
        </div>
      </div>
      {onAccept && onDecline && (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onAccept} disabled={busy} className="btn btn--gold" style={{ padding: '8px 14px', fontSize: 10 }}>ACCEPT</button>
          <button onClick={onDecline} disabled={busy} className="btn" style={{ padding: '8px 14px', fontSize: 10 }}>DECLINE</button>
        </div>
      )}
    </div>
  );
}

function InviteRow({ i }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid rgba(236,231,218,0.12)' }}>
      <div className="portal-avatar">
        {i.avatarUrl ? <img src={i.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {i.handle ? (
          <Link to={`/${i.handle}`} className="portal-serif" style={{ fontSize: 15, color: '#ECE7DA', textDecoration: 'none' }}>{i.displayName}</Link>
        ) : (
          <span className="portal-serif" style={{ fontSize: 15 }}>{i.displayName}</span>
        )}
        <div className="portal-mono" style={{ fontSize: 10, color: 'rgba(236,231,218,0.34)' }}>
          {i.handle ? `@${i.handle}` : ''}{i.location ? `${i.handle ? ' · ' : ''}${i.location.toUpperCase()}` : ''} · INVITED
        </div>
      </div>
    </div>
  );
}
